import { Tooltip } from "antd";
import styled from "styled-components";
import InfoCircle from "@untitled-ui/icons-react/build/esm/InfoCircle";

// #region SCOPED CSS
const FormStyles = styled.div`
    .simple-label-field {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        align-self: stretch;
    }

    .simple-label-field .FormComponentContainer {
        width: 100%;
        height: 100%;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 4px;
        display: inline-flex;
        position: relative;
    }

    .simple-label-field .Frame51 {
        align-self: stretch;
        padding-left: 5px;
        padding-right: 5px;
        justify-content: flex-start;
        align-items: center;
        gap: 4px;
        display: inline-flex;
    }

    .simple-label-field .Icon {
        width: 11px;
        height: 11px;
        color: var(--text-icons-gray-200);
        position: relative;
        top: -1px;
    }

    .simple-label-field .Icon.locked,
    .simple-label-field .Icon.mandatoryLocked {
        color: var(--text-icons-gray-300);
    }

    .simple-label-field .Icon.disabledSimple {
        color: var(--text-icons-gray-200);
    }

    .simple-label-field .Title {
        color: var(--text-icons-gray-200);
        font-size: 0.75rem;
        font-weight: 400;
        word-wrap: break-word;
    }

    .simple-label-field .InputSelectorInverted {
        align-self: stretch;
        justify-content: flex-start;
        align-items: flex-start;
        display: flex;
    }

    .simple-label-field .Search {
        flex: 1 1 0;
        height: var(--simple-label-field-height, 118px);
        justify-content: flex-start;
        align-items: flex-start;
        gap: 8px;
        display: flex;
    }

    .simple-label-field .Frame48 {
        width: 100%;
        flex: 1 1 0;
        height: var(--simple-label-field-height, 118px);
        background: var(--backgrounds-lines-n-600);
        border-radius: 4px;
        border: 0;
        border-left: 5px solid var(--backgrounds-lines-n-500);
        outline: none;
        color: var(--text-icons-gray-100);
        gap: 0;
    }

    .simple-label-field .Frame48.mandatory {
        border-left: 5px solid var(--text-icons-orange-500);
    }

    .simple-label-field .Frame48.recommended {
        border-left: 5px solid var(--text-icons-blue-500);
    }

    .simple-label-field .Frame48.locked,
    .simple-label-field .Frame48.mandatoryLocked {
        border-left: 5px solid var(--text-icons-gray-300);
        cursor: not-allowed !important;
    }

    .simple-label-field .Frame48.disabledSimple {
        border-left: 5px solid var(--backgrounds-lines-n-500, #141922);
    }

    .simple-label-field .simple-label-content {
        flex: 1 1 0;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        padding: 10px 12px 6px 12px;
        background: var(--backgrounds-lines-n-600);
        border: 0;
        outline: none;
        color: var(--text-icons-green-500);
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        overflow: auto;
        overflow-wrap: anywhere;
        word-break: normal;
    }

    .simple-label-field .simple-label-content.placeholder {
        color: var(--text-icons-gray-300) !important;
        font-style: italic;
    }

    .simple-label-field .simple-label-content p {
        margin: 0 0 6px 0;
    }

    .simple-label-field .simple-label-content p:last-child {
        margin-bottom: 0;
    }

    .simple-label-field .simple-label-content ul,
    .simple-label-field .simple-label-content ol {
        margin: 0 0 6px 18px;
        padding: 0;
    }

    .simple-label-field .simple-label-content svg {
        max-width: 100%;
        height: auto;
        color: currentColor;
        fill: currentColor;
        vertical-align: middle;
    }

    .simple-label-field .simple-label-content svg [stroke]:not([stroke="none"]) {
        stroke: currentColor !important;
    }

    .simple-label-field .simple-label-content svg [fill]:not([fill="none"]) {
        fill: currentColor !important;
    }

    .simple-label-field .Frame50 {
        align-self: stretch;
        padding-left: 5px;
        padding-right: 5px;
        text-align: right;
    }

    .simple-label-field .AssistiveText {
        font-size: 8px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 0.1px;
        text-transform: uppercase;
        height: 9px;
    }
`;

export default function SimpleLabelField({
    className = "",
    title,
    varName,
    value,
    placeholder = "",
    assistiveText = "",
    assistiveTextColorOverride = "",
    formFieldColumSize = 1,
    styling = {},
    requiredType = "simple",
    tooltip,
    tooltipMaxWidth = "200px",
    style = {}
}) {
    const hasValue = value !== undefined && value !== null && `${value}`.trim() !== "";
    const displayValue = hasValue ? `${value}` : "";
    const fieldHeight = styling?.formFieldHeight ?? "118px";
    const textColor =
        requiredType === "locked" || requiredType === "mandatoryLocked"
            ? "var(--text-icons-gray-300)"
            : "var(--text-icons-green-500)";
    const assistiveTextColor = assistiveTextColorOverride || "var(--text-icons-blue-500)";
    const disableTooltip = !tooltip && !title;
    const disableAssistiveText = !assistiveText;

    return (
        <FormStyles>
            <div
                className={`FormField simple-label-field ${className} ${requiredType}`}
                style={{
                    ...style,
                    "--simple-label-field-height": fieldHeight,
                    "gridColumn": `span ${formFieldColumSize}`,
                    "width": styling?.formFieldWidth ?? style?.width,
                    "maxWidth": styling?.formFieldMaxWidth ?? style?.maxWidth,
                    "minWidth": styling?.formFieldMinWidth ?? style?.minWidth
                }}>
                <div className="FormComponentContainer">
                    <div className={`Frame51 ${disableTooltip ? "hidden" : ""}`}>
                        {tooltip && (
                            <Tooltip
                                title={tooltip}
                                color="#10141b"
                                overlayInnerStyle={{ textAlign: "center" }}
                                overlayStyle={{ fontSize: "0.75rem", maxWidth: tooltipMaxWidth, textAlign: "center" }}>
                                <InfoCircle className={`Icon ${requiredType} text-align-center`} aria-hidden="true" />
                            </Tooltip>
                        )}
                        {!tooltip && <InfoCircle className="Icon hidden" aria-hidden="true" />}
                        <div
                            className={`Title ${
                                requiredType === "disabledSimple"
                                    ? "text-gray-200"
                                    : requiredType === "locked" || requiredType === "mandatoryLocked"
                                      ? "text-gray-300"
                                      : ""
                            }`}>
                            {title}
                        </div>
                    </div>
                    <div className="InputSelectorInverted">
                        <div className={`Search Frame48 ${requiredType}`}>
                            {hasValue ? (
                                <div
                                    id={varName ? `simple-label-${varName}` : undefined}
                                    className="simple-label-content"
                                    style={{ color: textColor }}
                                    aria-label={title}
                                    dangerouslySetInnerHTML={{ __html: displayValue }}
                                />
                            ) : (
                                <div
                                    id={varName ? `simple-label-${varName}` : undefined}
                                    className="simple-label-content placeholder"
                                    aria-label={title}>
                                    {placeholder}
                                </div>
                            )}
                        </div>
                    </div>
                    <div
                        className={`Frame50 ${disableAssistiveText ? "hidden" : ""}`}
                        style={{ color: assistiveTextColor }}>
                        <div className="AssistiveText" dangerouslySetInnerHTML={{ __html: assistiveText }}></div>
                    </div>
                </div>
            </div>
        </FormStyles>
    );
}
